import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import toast from 'react-hot-toast';
import { Sheet } from '../ui/Sheet';
import { X, CheckCircle2, AlertTriangle, MapPin, ChevronDown } from 'lucide-react';
import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { EVENT_TYPES } from '@/constants/eventTypes';
import { toEventTypeDisplayLabel } from '@/utils/eventTypeDisplay';
import { cn } from '@/utils/cn';
import type { ExperienceOption } from './ExperienceQuestionsCard';
import {
  ExperienceDetailsForm,
  MAX_PAYMENT_AMOUNT,
  isValidPerformanceDurationMins,
  type ExperienceDetailsAnswers,
} from './ExperienceDetailsForm';
import {
  isEmptyGoogleAutocompleteResponse,
  mapGoogleAutocompleteSuggestions,
} from './googlePlacesAutocomplete';
import { useMobileFieldScroll } from '@/utils/mobileFieldScroll';

type Coords = { lat: number; lng: number };

type DrawerSection = 'location' | 'gig';
type OpenSection = DrawerSection | null;

type NominatimAddress = {
  pincode: string;
  city: string;
  state: string;
};

type PlacePrediction = {
  placeId: string;
  description: string;
  source?: 'google' | 'nominatim';
  nominatimAddress?: NominatimAddress;
};

type NominatimSearchResult = {
  place_id?: number;
  lat?: string;
  lon?: string;
  display_name?: string;
  address?: Record<string, string>;
};

const NOMINATIM_USER_AGENT = 'starclinch-app';
const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';

const isNominatimPlaceId = (placeId: string) => placeId.startsWith('nominatim:');

const parseNominatimAddress = (
  item: NominatimSearchResult,
  fallbackPincode: string
): NominatimAddress => {
  const addr = item.address ?? {};
  return {
    pincode: (addr.postcode || fallbackPincode).trim(),
    city: (addr.city || addr.town || addr.village || addr.suburb || addr.county || '').trim(),
    state: (addr.state || '').trim(),
  };
};

const mapNominatimResultsToPredictions = (
  results: NominatimSearchResult[],
  fallbackPincode: string
): PlacePrediction[] =>
  results
    .map((item) => {
      const lat = Number(item.lat);
      const lng = Number(item.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

      const nominatimAddress = parseNominatimAddress(item, fallbackPincode);
      const description =
        item.display_name ||
        [nominatimAddress.pincode, nominatimAddress.city, nominatimAddress.state]
          .filter((part) => part.trim().length > 0)
          .join(', ');

      if (!description.trim()) return null;

      const placeId =
        item.place_id != null
          ? `nominatim:${item.place_id}`
          : `nominatim:${lat},${lng}`;

      return {
        placeId,
        description,
        source: 'nominatim' as const,
        nominatimAddress,
      };
    })
    .filter((item): item is PlacePrediction => !!item);

async function fetchNominatimPincodeSuggestions(pincode: string): Promise<PlacePrediction[]> {
  const params = new URLSearchParams({
    postalcode: pincode,
    countrycodes: 'in',
    format: 'jsonv2',
    addressdetails: '1',
  });

  const res = await fetch(`${NOMINATIM_SEARCH_URL}?${params}`, {
    headers: { 'User-Agent': NOMINATIM_USER_AGENT },
  });

  if (!res.ok) return [];

  const data = await res.json();
  const results = Array.isArray(data) ? (data as NominatimSearchResult[]) : [];
  return mapNominatimResultsToPredictions(results, pincode);
}

declare global {
  interface Window {
    google?: any;
  }
}

const isValidPincode = (p: string) => /^\d{6}$/.test(p.trim());
const isValidCityOrState = (s: string) => /^[A-Za-z\s]+$/.test(s.trim());

// Match UserHome credibility logic: event type slugs from EVENT_TYPES.
const FALLBACK_EVENT_OPTIONS: ExperienceOption[] = EVENT_TYPES.map((item) => ({
  value: item.e_slug,
  label: toEventTypeDisplayLabel(item.event_name),
}));

const INITIAL_EXPERIENCE_DETAILS: ExperienceDetailsAnswers = {
  firstPaidGigYear: null,
  eventAbout: null,
  performanceLocation: null,
  firstClientName: '',
  paymentMethod: null,
  hasPaymentProof: null,
  paymentAmount: '',
  performanceDurationMins: '',
};

const PAYMENT_MODE_LABEL_MAP: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  upi: 'UPI',
  cheque: 'Cheque',
};

function safeParseLocalJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

async function postExpressBookingLocationOnly(payload: {
  pincode: string;
  city: string;
  state: string;
  complete_address: string;
  landmark?: string;
}) {
  await apiClient.post(API_ENDPOINTS.EXPRESS_BOOKING.LOCATIONS, payload);
}

async function postExpressBookingLocation(payload: {
  pincode: string;
  city: string;
  state: string;
  complete_address: string;
  landmark?: string;
  first_gig: number;
  where: string;
  event_type: string;
  performed_for: string;
  first_payment_mode: string;
  first_payment_proof: boolean;
  first_payment_rupees: string;
  first_duration_mins: number;
}) {
  await apiClient.post(API_ENDPOINTS.EXPRESS_BOOKING.BASE, payload);
  const detailsRes = await apiClient.get(API_ENDPOINTS.AUTH.ARTIST_DETAILS);
  const details = detailsRes?.data?.data;
  if (details) {
    useArtistDetailsStore.getState().setArtistDetails(details);
  }
}

type ArtistCredibilityData = {
  first_gig: number | null;
  where: string | null;
  event_type: string | { slug: string; name: string } | null;
  performed_for: string | null;
  first_payment_mode?: string | null;
  first_payment_proof?: boolean | null;
  first_payment_rupees?: string | number | null;
  first_duration_mins?: number | null;
};

type ArtistCredibilityResponse = {
  success: boolean;
  data: ArtistCredibilityData;
};

const EMPTY_CREDIBILITY_SNAPSHOT: ArtistCredibilityData = {
  first_gig: null,
  where: null,
  event_type: null,
  performed_for: null,
  first_payment_mode: null,
  first_payment_proof: null,
  first_payment_rupees: null,
  first_duration_mins: null,
};

const normalizeNullableString = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed ? trimmed : null;
};

const parseEventType = (
  eventType: ArtistCredibilityData['event_type']
): { slug: string | null; name: string | null } => {
  if (typeof eventType === 'string') {
    return { slug: normalizeNullableString(eventType), name: null };
  }
  if (eventType && typeof eventType === 'object') {
    return {
      slug: normalizeNullableString((eventType as { slug?: string }).slug),
      name: normalizeNullableString((eventType as { name?: string }).name),
    };
  }
  return { slug: null, name: null };
};

async function fetchArtistCredibility(): Promise<ArtistCredibilityData> {
  const res = await apiClient.get<ArtistCredibilityResponse>(API_ENDPOINTS.AUTH.ARTIST_CREDIBILITY);
  if (!res.data?.success || !res.data.data) {
    return { ...EMPTY_CREDIBILITY_SNAPSHOT };
  }
  return res.data.data;
}

const mapPaymentModeToExperienceSlug = (mode: string | null | undefined): string | null => {
  const trimmed = mode?.trim() ?? '';
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (PAYMENT_MODE_LABEL_MAP[lower]) return lower;
  const normalizedSlug = lower.replace(/\s+/g, '_');
  if (PAYMENT_MODE_LABEL_MAP[normalizedSlug]) return normalizedSlug;
  const slugEntry = Object.entries(PAYMENT_MODE_LABEL_MAP).find(
    ([, label]) => label.toLowerCase() === lower
  );
  return slugEntry?.[0] ?? null;
};

const mapPaymentProofToExperienceValue = (
  proof: boolean | null | undefined
): string | null => {
  if (proof === true) return 'yes';
  if (proof === false) return 'no';
  return null;
};

const normalizeCredibilityPaymentAmountDigits = (
  rupees: string | number | null | undefined
): string => {
  if (rupees === null || rupees === undefined) return '';
  const amount = Number(String(rupees).replace(/[^\d.]/g, ''));
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return String(Math.round(amount));
};

const normalizeCredibilityDurationMins = (
  mins: number | null | undefined
): string => {
  if (mins === null || mins === undefined || !Number.isFinite(mins)) return '';
  return String(Math.round(mins));
};

type CredibilityGigPayload = {
  first_gig: number;
  where: string;
  event_type: string;
  performed_for: string;
  first_payment_mode: string;
  first_payment_proof: boolean;
  first_payment_rupees: string;
  first_duration_mins: number;
};

function mapCredibilityToExperienceAnswers(
  data: ArtistCredibilityData
): ExperienceDetailsAnswers {
  const { slug: nextEventTypeSlug } = parseEventType(data.event_type);
  return {
    firstPaidGigYear: data.first_gig !== null ? String(data.first_gig) : null,
    eventAbout: nextEventTypeSlug,
    performanceLocation: data.where?.trim() || null,
    firstClientName: normalizeNullableString(data.performed_for) ?? '',
    paymentMethod: mapPaymentModeToExperienceSlug(data.first_payment_mode),
    hasPaymentProof: mapPaymentProofToExperienceValue(data.first_payment_proof),
    paymentAmount: normalizeCredibilityPaymentAmountDigits(data.first_payment_rupees),
    performanceDurationMins: normalizeCredibilityDurationMins(data.first_duration_mins),
  };
}

const iosEasing: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

const DrawerAccordionPanel: React.FC<{
  open: boolean;
  allowOverflow?: boolean;
  onOpenComplete?: () => void;
  onCloseComplete?: () => void;
  children: React.ReactNode;
}> = ({ open, allowOverflow = false, onOpenComplete, onCloseComplete, children }) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [overflowUnlocked, setOverflowUnlocked] = useState(false);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const measure = () => setContentHeight(el.scrollHeight);
    measure();

    const observer = new ResizeObserver(() => {
      if (open) measure();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, children]);

  useEffect(() => {
    if (!open) setOverflowUnlocked(false);
  }, [open]);

  return (
    <motion.div
      initial={false}
      animate={{
        height: open ? contentHeight : 0,
        opacity: open ? 1 : 0,
      }}
      transition={{
        height: { duration: 0.4, ease: iosEasing },
        opacity: open
          ? { duration: 0.28, delay: 0.06, ease: 'easeOut' }
          : { duration: 0.18, ease: 'easeIn' },
      }}
      onAnimationComplete={() => {
        if (open) {
          setOverflowUnlocked(true);
          onOpenComplete?.();
        } else {
          onCloseComplete?.();
        }
      }}
      className={cn(
        'overflow-hidden',
        allowOverflow && open && overflowUnlocked && 'overflow-visible'
      )}
    >
      <div ref={innerRef} className="pt-4">
        {children}
      </div>
    </motion.div>
  );
};

export const EnableExpressBookingDrawer: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccessNavigate: () => void;
  onSaveSuccess?: () => Promise<void> | void;
  /** When true (Add New Location flow), hide experience card and use Home credibility prefill. */
  hideExperienceQuestions?: boolean;
}> = ({ open, onOpenChange, onSuccessNavigate, onSaveSuccess, hideExperienceQuestions = false }) => {
  const googleApiKey = useMemo(() => import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '', []);
  const isNativeApp = Capacitor.isNativePlatform();
  const [isGoogleMapsScriptLoaded, setIsGoogleMapsScriptLoaded] = useState(false);
  const artistCategoryName = useArtistDetailsStore((s) => s.data?.artist_category?.name);
  const ensureArtistDetailsFetched = useArtistDetailsStore((s) => s.ensureFetched);

  const [locationQuery, setLocationQuery] = useState('');
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>([]);
  const [isFetchingPredictions, setIsFetchingPredictions] = useState(false);
  const [predictionsOpen, setPredictionsOpen] = useState(false);

  // Address payload fields (pincode/city/state filled after reverse geocode)
  const [pincode, setPincode] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [completeAddress, setCompleteAddress] = useState('');
  const [landmark, setLandmark] = useState('');

  const [mapOpen, setMapOpen] = useState(false);
  const [mapCoords, setMapCoords] = useState<Coords | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [allSetOpen, setAllSetOpen] = useState(false);
  const [experienceAnswers, setExperienceAnswers] = useState<ExperienceDetailsAnswers>(
    INITIAL_EXPERIENCE_DETAILS
  );
  const [experienceResetKey, setExperienceResetKey] = useState(0);
  const [eventTypeOptions] = useState<ExperienceOption[]>(
    FALLBACK_EVENT_OPTIONS
  );
  const [isCredibilityPrefillLoading, setIsCredibilityPrefillLoading] = useState(false);
  const [openSection, setOpenSection] = useState<OpenSection>('location');
  const [locationPanelExpanded, setLocationPanelExpanded] = useState(false);
  const navTimerRef = useRef<number | null>(null);
  const hasAutoAdvancedRef = useRef(false);
  const landmarkInputRef = useRef<HTMLInputElement | null>(null);
  const confirmedPlaceIdRef = useRef<string | null>(null);
  const pendingPlaceIdRef = useRef<string | null>(null);
  const suppressAutocompleteRef = useRef(false);
  const locationInputContainerRef = useRef<HTMLDivElement | null>(null);
  const drawerScrollRootRef = useRef<HTMLDivElement | null>(null);
  const predictionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [predictionsMenuStyle, setPredictionsMenuStyle] = useState<React.CSSProperties>({});

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [mapContainerReady, setMapContainerReady] = useState(false);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const scriptLoadPromiseRef = useRef<Promise<void> | null>(null);

  const clearTimer = () => {
    if (navTimerRef.current) {
      window.clearTimeout(navTimerRef.current);
      navTimerRef.current = null;
    }
  };

  const isExperienceComplete =
    !!experienceAnswers.firstPaidGigYear &&
    !!experienceAnswers.eventAbout &&
    !!experienceAnswers.performanceLocation?.trim() &&
    !!experienceAnswers.firstClientName.trim() &&
    !!experienceAnswers.paymentMethod &&
    !!experienceAnswers.hasPaymentProof &&
    !!experienceAnswers.paymentAmount.trim() &&
    isValidPerformanceDurationMins(experienceAnswers.performanceDurationMins);

  const isServiceLocationConfirmed = Boolean(pincode && city && state);
  const isLocationSectionComplete =
    isServiceLocationConfirmed && completeAddress.trim().length >= 10;
  const requiresDrawerExperience = !hideExperienceQuestions;
  const canShowSaveButton = hideExperienceQuestions
    ? isLocationSectionComplete
    : isServiceLocationConfirmed;
  const canSubmitSave = hideExperienceQuestions
    ? isLocationSectionComplete
    : isExperienceComplete;

  useMobileFieldScroll(drawerScrollRootRef, { extraBottomInset: 88 });

  useEffect(() => {
    if (!open) {
      clearTimer();
      setAllSetOpen(false);
      setMapOpen(false);
      setPredictionsOpen(false);
      setExperienceAnswers(INITIAL_EXPERIENCE_DETAILS);
      setIsCredibilityPrefillLoading(false);
      setOpenSection('location');
      setLocationPanelExpanded(false);
      hasAutoAdvancedRef.current = false;
      confirmedPlaceIdRef.current = null;
      pendingPlaceIdRef.current = null;
      suppressAutocompleteRef.current = false;
      return;
    }

    void ensureArtistDetailsFetched();

    // Every time the drawer opens, start from a clean "location not confirmed" state
    // so the below fields (address + questions) don't show prematurely from stale state.
    resetLocationFieldsButKeepAddress();
    setLocationQuery('');
    setCompleteAddress('');
    setLandmark('');
    setPredictionsOpen(false);
    setExperienceResetKey((v) => v + 1);
    setOpenSection('location');
    setLocationPanelExpanded(false);
    hasAutoAdvancedRef.current = false;
    confirmedPlaceIdRef.current = null;
    pendingPlaceIdRef.current = null;

    // Prefill experience answers from credibility API (initial enable flow only).
    if (!hideExperienceQuestions) {
      setIsCredibilityPrefillLoading(true);
      (async () => {
        try {
          const next = await fetchArtistCredibility();
          setExperienceAnswers(mapCredibilityToExperienceAnswers(next));
        } catch {
          // silent: drawer still works with empty defaults
        } finally {
          setIsCredibilityPrefillLoading(false);
        }
      })();
    }
    return () => {
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || openSection !== 'location') {
      setLocationPanelExpanded(false);
      return;
    }
    // Fallback: initial={false} may skip animation so onOpenComplete never runs
    const timer = window.setTimeout(() => setLocationPanelExpanded(true), 0);
    return () => window.clearTimeout(timer);
  }, [open, openSection]);

  const tryAdvanceToGigSection = () => {
    if (!requiresDrawerExperience || hasAutoAdvancedRef.current) return;
    if (!isLocationSectionComplete) return;
    hasAutoAdvancedRef.current = true;
    setOpenSection('gig');
  };

  const handleCompleteAddressBlur = () => {
    window.setTimeout(() => {
      if (document.activeElement === landmarkInputRef.current) return;
      tryAdvanceToGigSection();
    }, 0);
  };

  const loadGoogleMapsScript = async () => {
    if (typeof window === 'undefined') return;
    if (window.google?.maps) {
      setIsGoogleMapsScriptLoaded(true);
      return;
    }
    if (!googleApiKey) {
      toast.error('Google Maps API key is missing (VITE_GOOGLE_MAPS_API_KEY).');
      return;
    }
    if (scriptLoadPromiseRef.current) return scriptLoadPromiseRef.current;

    scriptLoadPromiseRef.current = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-google-maps-script="true"]');
      if (existing) {
        // If script tag exists but google isn't ready yet, wait for it.
        existing.addEventListener('load', () => {
          setIsGoogleMapsScriptLoaded(true);
          resolve();
        });
        existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps script.')));
        return;
      }

      const script = document.createElement('script');
      script.setAttribute('data-google-maps-script', 'true');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        googleApiKey
      )}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsGoogleMapsScriptLoaded(true);
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load Google Maps script.'));
      document.head.appendChild(script);
    });

    return scriptLoadPromiseRef.current;
  };

  const googleMapsReady = () => !!window.google?.maps?.Map;

  // Load google maps JS only when the map UI is actually opened
  useEffect(() => {
    if (!mapOpen) return;
    loadGoogleMapsScript().catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapOpen]);

  const predictionsAbortRef = useRef<number | null>(null);
  useEffect(() => {
    if (!open) return;
    if (suppressAutocompleteRef.current) {
      setIsFetchingPredictions(false);
      return;
    }
    if (!locationQuery.trim() || locationQuery.trim().length < 2) {
      setPlacePredictions([]);
      setIsFetchingPredictions(false);
      return;
    }

    const input = locationQuery.trim();
    const querySnapshot = input;
    setIsFetchingPredictions(true);

    // Lightweight debounce to avoid spamming the API
    if (predictionsAbortRef.current) window.clearTimeout(predictionsAbortRef.current);
    predictionsAbortRef.current = window.setTimeout(() => {
      (async () => {
        if (!googleApiKey) {
          setIsFetchingPredictions(false);
          setPlacePredictions([]);
          return;
        }

        try {
          const autocompleteUrl = isNativeApp
            ? 'https://places.googleapis.com/v1/places:autocomplete'
            : '/api/google-places-autocomplete';
          const res = await fetch(autocompleteUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': googleApiKey,
              'X-Goog-FieldMask':
                'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
            },
            body: JSON.stringify({
              input: querySnapshot,
              includedRegionCodes: ['in'],
            }),
          });

          if (!res.ok) {
            setPlacePredictions([]);
            setIsFetchingPredictions(false);
            return;
          }

          const data = await res.json();

          let mapped: PlacePrediction[] = mapGoogleAutocompleteSuggestions(data).map((item) => ({
            ...item,
            source: 'google' as const,
          }));

          // Only when Google returns {} / no suggestions — fall back to Nominatim for pincodes.
          if (
            mapped.length === 0 &&
            isEmptyGoogleAutocompleteResponse(data) &&
            isValidPincode(querySnapshot)
          ) {
            mapped = await fetchNominatimPincodeSuggestions(querySnapshot);
          }

          if (querySnapshot !== locationQuery.trim()) return; // ignore stale results
          setPlacePredictions(mapped);
          setIsFetchingPredictions(false);
        } catch {
          setPlacePredictions([]);
          setIsFetchingPredictions(false);
        }
      })();
    }, 250);

    return () => {
      if (predictionsAbortRef.current) window.clearTimeout(predictionsAbortRef.current);
    };
  }, [googleApiKey, isNativeApp, locationQuery, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const shouldShowPredictionsMenu =
    !suppressAutocompleteRef.current &&
    predictionsOpen &&
    !isServiceLocationConfirmed &&
    (isFetchingPredictions || placePredictions.length > 0);

  useEffect(() => {
    if (!shouldShowPredictionsMenu) return;

    const updatePredictionsMenuPosition = () => {
      const container = locationInputContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const availableBelow = viewportHeight - rect.bottom - 12;
      const availableAbove = rect.top - 12;
      const shouldOpenUp = availableBelow < 180 && availableAbove > availableBelow;
      const maxHeight = Math.min(240, Math.max(120, shouldOpenUp ? availableAbove : availableBelow));

      setPredictionsMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        top: shouldOpenUp ? undefined : rect.bottom + 8,
        bottom: shouldOpenUp ? viewportHeight - rect.top + 8 : undefined,
        maxHeight,
        zIndex: 1100,
        pointerEvents: 'auto',
      });
    };

    updatePredictionsMenuPosition();

    const onWindowChange = () => updatePredictionsMenuPosition();
    const onScrollCapture = (event: Event) => {
      const target = event.target as Node | null;
      if (target && predictionsMenuRef.current?.contains(target)) return;
      updatePredictionsMenuPosition();
    };

    window.addEventListener('resize', onWindowChange);
    window.addEventListener('scroll', onScrollCapture, true);

    return () => {
      window.removeEventListener('resize', onWindowChange);
      window.removeEventListener('scroll', onScrollCapture, true);
    };
  }, [shouldShowPredictionsMenu, placePredictions.length, isFetchingPredictions]);

  const resetLocationFieldsButKeepAddress = () => {
    setPlacePredictions([]);
    setPredictionsOpen(false);
    setLocationQuery('');
    setMapOpen(false);
    setMapCoords(null);
    setPincode('');
    setCity('');
    setState('');
    confirmedPlaceIdRef.current = null;
    pendingPlaceIdRef.current = null;
    suppressAutocompleteRef.current = false;
  };

  const getAddressComponentText = (component: any) =>
    String(component?.longText ?? component?.long_name ?? component?.shortText ?? '').trim();

  const parseAddressComponents = (addressComponents: any[] | undefined) => {
    const comps = Array.isArray(addressComponents) ? addressComponents : [];
    const findByType = (type: string) => {
      const found = comps.find((c) => Array.isArray(c.types) && c.types.includes(type));
      return found ? getAddressComponentText(found) : '';
    };

    // Old doc mapping:
    // postal_code -> pincode
    // locality -> city
    // administrative_area_level_1 -> state
    const postalCode = findByType('postal_code');
    const locality = findByType('locality');
    const admin2 = findByType('administrative_area_level_2');
    const admin1 = findByType('administrative_area_level_1');

    const cityCandidate = locality || admin2;

    return {
      pincode: postalCode,
      city: cityCandidate,
      state: admin1,
    };
  };

  const applyNominatimSelection = (pred: PlacePrediction): boolean => {
    const addr = pred.nominatimAddress;
    if (!addr) {
      toast.error('Could not read location details for this pincode.');
      return false;
    }

    const nextPincode = addr.pincode.trim();
    const nextCity = addr.city.trim();
    const nextState = addr.state.trim();

    if (!isValidPincode(nextPincode)) {
      toast.error('Could not determine a valid pincode for this location.');
      return false;
    }
    if (!nextCity || !isValidCityOrState(nextCity)) {
      toast.error('Could not determine a valid city for this location.');
      return false;
    }
    if (!nextState || !isValidCityOrState(nextState)) {
      toast.error('Could not determine a valid state for this location.');
      return false;
    }

    setPincode(nextPincode);
    setCity(nextCity);
    setState(nextState);
    setOpenSection('location');
    confirmedPlaceIdRef.current = pred.placeId;
    return true;
  };

  const handleSelectPrediction = async (pred: PlacePrediction) => {
    suppressAutocompleteRef.current = true;
    setLocationQuery(pred.description);
    setPredictionsOpen(false);
    setPlacePredictions([]);

    if (pred.source === 'nominatim' || isNominatimPlaceId(pred.placeId)) {
      applyNominatimSelection(pred);
      return;
    }

    if (
      pred.placeId &&
      pred.placeId === confirmedPlaceIdRef.current &&
      pincode &&
      city &&
      state
    ) {
      return;
    }

    pendingPlaceIdRef.current = pred.placeId;

    if (!googleApiKey) {
      toast.error('Google Maps API key is not set.');
      return;
    }

    try {
      // Ensure the Google JS script is loaded before toggling mapOpen.
      await loadGoogleMapsScript();

      setMapCoords(null);
      setMapOpen(false);

      const placeDetailsUrl = isNativeApp
        ? `https://places.googleapis.com/v1/places/${encodeURIComponent(pred.placeId)}`
        : `/api/google-place-details/${encodeURIComponent(pred.placeId)}`;
      const res = await fetch(placeDetailsUrl, {
        method: 'GET',
        headers: {
          'X-Goog-Api-Key': googleApiKey,
          'X-Goog-FieldMask':
            'id,displayName,formattedAddress,location,addressComponents',
        },
      });

      if (!res.ok) {
        toast.error('Could not fetch place details. Please try another suggestion.');
        return;
      }

      const details = await res.json();
      const lat = details?.location?.latitude;
      const lng = details?.location?.longitude;

      if (typeof lat !== 'number' || typeof lng !== 'number') {
        toast.error('Could not find coordinates for this place.');
        return;
      }

      const parsed = parseAddressComponents(details?.addressComponents);
      const nextPincode = parsed.pincode.trim();
      const nextCity = parsed.city.trim();
      const nextState = parsed.state.trim();

      if (
        isValidPincode(nextPincode) &&
        nextCity &&
        isValidCityOrState(nextCity) &&
        nextState &&
        isValidCityOrState(nextState)
      ) {
        setPincode(nextPincode);
        setCity(nextCity);
        setState(nextState);
        confirmedPlaceIdRef.current = pred.placeId;
        setPredictionsOpen(false);
        setPlacePredictions([]);
      }

      setMapCoords({ lat, lng });
      setMapOpen(true);
    } catch {
      toast.error('Something went wrong while opening the map.');
    }
  };

  // Initialize / update map instance when map modal opens
  useEffect(() => {
    if (!mapOpen) return;
    if (!mapCoords) return;
    if (!mapContainerReady) return;
    if (!googleMapsReady()) return;
    if (!mapContainerRef.current) return;

    const mapEl = mapContainerRef.current;
    const { lat, lng } = mapCoords;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapEl, {
        center: { lat, lng },
        zoom: 16,
        disableDefaultUI: true,
        clickableIcons: false,
      });
    } else {
      mapInstanceRef.current.setCenter({ lat, lng });
    }

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapInstanceRef.current,
        draggable: true,
      });
    } else {
      markerRef.current.setPosition({ lat, lng });
      markerRef.current.setMap(mapInstanceRef.current);
      markerRef.current.setOptions({ draggable: true });
      if (typeof markerRef.current.setDraggable === 'function') {
        markerRef.current.setDraggable(true);
      }
    }

    const marker = markerRef.current;
    const map = mapInstanceRef.current;

    const onDragEnd = () => {
      const pos = marker.getPosition();
      const next = pos
        ? {
          lat: pos.lat(),
          lng: pos.lng(),
        }
        : null;
      if (next) setMapCoords(next);
    };

    window.google.maps.event.clearListeners(marker, 'dragend');
    window.google.maps.event.clearListeners(marker, 'drag');
    window.google.maps.event.clearListeners(map, 'click');

    marker.addListener('dragend', onDragEnd);
    marker.addListener('drag', onDragEnd);
    map.addListener('click', (e: any) => {
      if (!e?.latLng) return;
      const next = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      marker.setPosition(next);
      setMapCoords(next);
    });

    return () => {
      // Keep instances around for performance, don't dispose.
    };
  }, [mapOpen, mapCoords, isGoogleMapsScriptLoaded, mapContainerReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmLocationFromMap = async () => {
    if (!mapCoords) {
      toast.error('Pick a location on the map first.');
      return;
    }
    if (!googleApiKey) {
      toast.error('Google Maps API key is not set.');
      return;
    }

    setIsSaving(true);
    try {
      const reverseGeocodeUrl = isNativeApp
        ? `https://maps.googleapis.com/maps/api/geocode/json?latlng=${encodeURIComponent(
            `${mapCoords.lat},${mapCoords.lng}`
          )}&key=${encodeURIComponent(googleApiKey)}`
        : `/api/google-reverse-geocode?latlng=${encodeURIComponent(
            `${mapCoords.lat},${mapCoords.lng}`
          )}&key=${encodeURIComponent(googleApiKey)}`;
      const res = await fetch(reverseGeocodeUrl);

      if (!res.ok) {
        toast.error('Could not reverse geocode. Please try another spot.');
        return;
      }

      const data = await res.json();
      const first = data?.results?.[0];
      const parsed = parseAddressComponents(first?.address_components);
      const nextPincode = parsed.pincode.trim();
      const nextCity = parsed.city.trim();
      const nextState = parsed.state.trim();

      if (!isValidPincode(nextPincode)) {
        toast.error('Could not determine a valid pincode for this location.');
        return;
      }
      if (!nextCity || !isValidCityOrState(nextCity)) {
        toast.error('Could not determine a valid city for this location.');
        return;
      }
      if (!nextState || !isValidCityOrState(nextState)) {
        toast.error('Could not determine a valid state for this location.');
        return;
      }

      setPincode(nextPincode);
      setCity(nextCity);
      setState(nextState);
      setMapOpen(false);
      setOpenSection('location');
      confirmedPlaceIdRef.current = pendingPlaceIdRef.current;
      setPredictionsOpen(false);
      setPlacePredictions([]);
      suppressAutocompleteRef.current = true;
    } catch {
      toast.error('Something went wrong while confirming location.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!pincode || !city || !state || !completeAddress.trim()) {
      toast.error('Please confirm your location details.');
      return;
    }
    if (!isValidPincode(pincode)) {
      toast.error('Enter a valid 6-digit pincode.');
      return;
    }
    if (!isValidCityOrState(city)) {
      toast.error('City can only contain letters and spaces.');
      return;
    }
    if (!isValidCityOrState(state)) {
      toast.error('State can only contain letters and spaces.');
      return;
    }
    const ca = completeAddress.trim();
    if (ca.length < 10 || ca.length > 300) {
      toast.error('Complete Address must be between 10 and 300 characters.');
      return;
    }
    if (requiresDrawerExperience && !isExperienceComplete) {
      toast.error('Please complete all experience questions.');
      return;
    }

    const locationPayload = {
      pincode,
      city,
      state,
      complete_address: ca,
      landmark: landmark.trim() ? landmark.trim() : undefined,
    };

    let gigPayload: CredibilityGigPayload | null = null;

    if (!hideExperienceQuestions) {
      const firstGigYear = Number(experienceAnswers.firstPaidGigYear);
      if (!Number.isFinite(firstGigYear)) {
        toast.error('Please select a valid first paid gig year.');
        return;
      }

      const paymentAmount = Number(experienceAnswers.paymentAmount);
      if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
        toast.error('Please enter a valid payment amount.');
        return;
      }
      if (paymentAmount > MAX_PAYMENT_AMOUNT) {
        toast.error('Payment amount cannot exceed 9,99,999.');
        return;
      }

      const performanceDuration = Number(experienceAnswers.performanceDurationMins);
      if (!isValidPerformanceDurationMins(experienceAnswers.performanceDurationMins)) {
        toast.error('Please enter a valid performance duration (10–480 mins).');
        return;
      }

      const credibilityWhere = experienceAnswers.performanceLocation?.trim() ?? '';
      const credibilityCategory = experienceAnswers.eventAbout?.trim() ?? '';
      const credibilityPerformedFor = experienceAnswers.firstClientName.trim();
      if (!credibilityWhere) {
        toast.error('Please select or enter a location in experience questions.');
        return;
      }
      if (!credibilityCategory) {
        toast.error('Please select a category in experience questions.');
        return;
      }
      if (!credibilityPerformedFor) {
        toast.error('Please enter the client / brand name in experience questions.');
        return;
      }

      const paymentModeValue = experienceAnswers.paymentMethod?.trim().toLowerCase() ?? '';
      const firstPaymentMode = PAYMENT_MODE_LABEL_MAP[paymentModeValue];
      if (!firstPaymentMode) {
        toast.error('Please select a valid payment mode.');
        return;
      }

      const paymentProofValue = experienceAnswers.hasPaymentProof?.trim().toLowerCase() ?? '';
      if (paymentProofValue !== 'yes' && paymentProofValue !== 'no') {
        toast.error('Please select whether payment proof is available.');
        return;
      }

      gigPayload = {
        first_gig: firstGigYear,
        where: credibilityWhere,
        event_type: credibilityCategory,
        performed_for: credibilityPerformedFor,
        first_payment_mode: firstPaymentMode,
        first_payment_proof: paymentProofValue === 'yes',
        first_payment_rupees: paymentAmount.toFixed(2),
        first_duration_mins: performanceDuration,
      };
    }

    setIsSaving(true);
    try {
      if (hideExperienceQuestions) {
        await postExpressBookingLocationOnly(locationPayload);
        await onSaveSuccess?.();

        localStorage.setItem('artist_service_location', JSON.stringify(locationPayload));
        const existingPins =
          safeParseLocalJson<string[]>(localStorage.getItem('artist_service_pincodes')) ?? [];
        const nextPins = Array.from(new Set([...existingPins, pincode]));
        localStorage.setItem('artist_service_pincodes', JSON.stringify(nextPins));
      } else {
        const payload = {
          ...locationPayload,
          ...gigPayload!,
        };

        await postExpressBookingLocation(payload);
        await onSaveSuccess?.();

        localStorage.setItem('artist_service_location', JSON.stringify(payload));
        const existingPins =
          safeParseLocalJson<string[]>(localStorage.getItem('artist_service_pincodes')) ?? [];
        const nextPins = Array.from(new Set([...existingPins, pincode]));
        localStorage.setItem('artist_service_pincodes', JSON.stringify(nextPins));
      }

      setAllSetOpen(true);
      clearTimer();
      navTimerRef.current = window.setTimeout(() => {
        onSuccessNavigate();
      }, 2500);

      // Keep drawer open for overlay; close after navigation unmount.
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
        'Failed to save your Express Booking location. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Sheet
        open={open}
        preventOutsideClose
        preventEscapeClose
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setAllSetOpen(false);
            setMapOpen(false);
            // Reset the drawer's location search UI when closing the drawer.
            resetLocationFieldsButKeepAddress();
            setPredictionsOpen(false);
            setExperienceAnswers(INITIAL_EXPERIENCE_DETAILS);
            setIsCredibilityPrefillLoading(false);
            setOpenSection('location');
            hasAutoAdvancedRef.current = false;
          }
          onOpenChange(nextOpen);
        }}
      >
        <div
          className="w-full text-white"
          style={{ pointerEvents: mapOpen ? 'none' : 'auto' }}
        >
          <div className="space-y-2 mb-5">
            <h2 className="text-2xl font-black tracking-tight">Enable Express Booking</h2>
            <p className="text-sm text-[#8E8E93]">
              To enable Express Booking, please share your location & Gig Info. where you can reliably perform on
              short notice.
            </p>
          </div>

          <div
            ref={drawerScrollRootRef}
            className={cn('space-y-4', canShowSaveButton && 'pb-24')}
          >
            {/* Your Location accordion */}
            <div
              className={cn(
                'rounded-2xl border border-white/10 bg-[#141416] px-4 py-4',
                shouldShowPredictionsMenu && 'relative z-30'
              )}
            >
              <button
                type="button"
                className="w-full flex items-center justify-between text-left"
                onClick={() => {
                  setOpenSection((current) => (current === 'location' ? null : 'location'));
                }}
                aria-expanded={openSection === 'location'}
              >
                <h3 className="text-base font-semibold text-white">Your Location</h3>
                <ChevronDown
                  className={`h-5 w-5 text-white/60 transition-transform duration-[400ms] ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
                    openSection === 'location' ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <DrawerAccordionPanel
                open={openSection === 'location'}
                allowOverflow={locationPanelExpanded && shouldShowPredictionsMenu}
                onOpenComplete={() => setLocationPanelExpanded(true)}
                onCloseComplete={() => setPredictionsOpen(false)}
              >
                <div className="space-y-4">
                  <div className="relative" ref={locationInputContainerRef}>
                    <div className="rounded-2xl bg-[#141416] border border-white/10 px-4 py-3 flex items-center gap-3">
                      <MapPin className="h-5 w-5 text-[#F33363]" />
                      <input
                        value={locationQuery}
                        onChange={(e) => {
                          suppressAutocompleteRef.current = false;
                          setLocationQuery(e.target.value);
                          setPredictionsOpen(true);
                        }}
                        onFocus={() => {
                          if (!suppressAutocompleteRef.current && !isServiceLocationConfirmed) {
                            setPredictionsOpen(true);
                          }
                        }}
                        placeholder="Type a location (e.g., Noida)"
                        className="flex-1 bg-transparent outline-none text-white placeholder:text-[#8E8E93] text-sm"
                      />
                      {locationQuery.trim().length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            resetLocationFieldsButKeepAddress();
                            setLocationQuery('');
                            setPredictionsOpen(false);
                          }}
                          className="rounded-full p-1 hover:bg-white/5 transition-colors"
                          aria-label="Clear location"
                        >
                          <X className="h-4 w-4 text-[#8E8E93]" />
                        </button>
                      )}
                    </div>

                    {shouldShowPredictionsMenu &&
                      typeof document !== 'undefined' &&
                      createPortal(
                        <div
                          ref={predictionsMenuRef}
                          style={predictionsMenuStyle}
                          className="rounded-2xl border border-white/10 bg-[#141416] shadow-2xl overflow-hidden"
                        >
                          <div
                            className="overflow-y-auto bg-[#141416]"
                            style={{
                              maxHeight:
                                typeof predictionsMenuStyle.maxHeight === 'number'
                                  ? predictionsMenuStyle.maxHeight
                                  : 240,
                              WebkitOverflowScrolling: 'touch',
                              overscrollBehavior: 'contain',
                              touchAction: 'pan-y',
                            }}
                          >
                            {isFetchingPredictions && (
                              <div className="px-4 py-3 text-sm text-[#8E8E93] bg-[#141416]">
                                Searching…
                              </div>
                            )}
                            {!isFetchingPredictions && placePredictions.length === 0 && (
                              <div className="px-4 py-3 text-sm text-[#8E8E93] bg-[#141416]">
                                No suggestions
                              </div>
                            )}
                            {placePredictions.map((pred) => (
                              <button
                                key={pred.placeId}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSelectPrediction(pred)}
                                className="w-full px-4 py-3 text-left bg-[#141416] hover:bg-white/5 transition-colors"
                              >
                                <div className="text-sm text-white">{pred.description}</div>
                              </button>
                            ))}
                          </div>
                        </div>,
                        document.body
                      )}
                  </div>

                  {isServiceLocationConfirmed && (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-2xl bg-[#141416] border border-white/10 px-4 py-3 text-center">
                          <div className="text-[11px] uppercase tracking-wide text-[#8E8E93] mb-1">
                            Pincode
                          </div>
                          <div className="text-sm text-white">{pincode}</div>
                        </div>
                        <div className="rounded-2xl bg-[#141416] border border-white/10 px-4 py-3 text-center">
                          <div className="text-[11px] uppercase tracking-wide text-[#8E8E93] mb-1">
                            City
                          </div>
                          <div className="text-sm text-white">{city}</div>
                        </div>
                        <div className="rounded-2xl bg-[#141416] border border-white/10 px-4 py-3 text-center">
                          <div className="text-[11px] uppercase tracking-wide text-[#8E8E93] mb-1">
                            State
                          </div>
                          <div className="text-sm text-white">{state}</div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <textarea
                          value={completeAddress}
                          onChange={(e) => setCompleteAddress(e.target.value)}
                          onBlur={handleCompleteAddressBlur}
                          placeholder="Complete Address"
                          rows={4}
                          className="w-full rounded-2xl bg-[#141416] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] outline-none resize-none"
                        />

                        <input
                          ref={landmarkInputRef}
                          value={landmark}
                          onChange={(e) => setLandmark(e.target.value)}
                          onBlur={tryAdvanceToGigSection}
                          placeholder="Landmark (Optional)"
                          className="w-full rounded-2xl bg-[#141416] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#8E8E93] outline-none"
                        />
                      </div>
                    </>
                  )}
                </div>
              </DrawerAccordionPanel>
            </div>

            {requiresDrawerExperience && (
              <div className="rounded-2xl border border-white/10 bg-[#141416] px-4 py-4">
                <button
                  type="button"
                  className={`w-full flex items-center justify-between text-left ${
                    !isLocationSectionComplete ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  onClick={() => {
                    if (!isLocationSectionComplete) return;
                    setOpenSection((current) => (current === 'gig' ? null : 'gig'));
                  }}
                  disabled={!isLocationSectionComplete}
                  aria-expanded={openSection === 'gig'}
                >
                  <h3 className="text-base font-semibold text-white">
                    FIRST Gig Info.
                  </h3>
                  <ChevronDown
                    className={`h-5 w-5 text-white/60 transition-transform duration-[400ms] ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
                      openSection === 'gig' ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <DrawerAccordionPanel open={openSection === 'gig'} allowOverflow={openSection === 'gig'}>
                  {!isCredibilityPrefillLoading && (
                    <ExperienceDetailsForm
                      categoryLabel={artistCategoryName || 'Artist'}
                      value={experienceAnswers}
                      onChange={setExperienceAnswers}
                      eventTypeOptions={eventTypeOptions}
                      resetKey={experienceResetKey}
                    />
                  )}
                </DrawerAccordionPanel>
              </div>
            )}

            {canShowSaveButton && (
              <button
                type="button"
                onClick={handleSave}
                disabled={
                  isSaving ||
                  (!hideExperienceQuestions && isCredibilityPrefillLoading) ||
                  !canSubmitSave
                }
                className="w-full rounded-full bg-[#F33363] px-6 py-4 text-sm font-bold text-white shadow-[0_12px_30px_rgba(243,51,99,0.45)] transition-transform hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
              >
                Save & Continue
              </button>
            )}

            {!googleApiKey && (
              <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-3 flex items-start gap-2 text-sm text-yellow-200">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <span>
                  Google Maps API key is not set. Location suggestions and map will not work.
                  (Set `VITE_GOOGLE_MAPS_API_KEY`.)
                </span>
              </div>
            )}
          </div>
        </div>
      </Sheet>

      {/* Map Modal (portal to ensure it always sits above the drawer + overlay) */}
      {mapOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm pointer-events-auto">
            <div className="absolute inset-0 flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="h-5 w-5 text-[#F33363]" />
                  <span className="font-semibold">Confirm your service location</span>
                </div>
                <button
                  type="button"
                  className="rounded-full p-2 hover:bg-white/10 transition-colors"
                  onClick={() => {
                    setMapOpen(false);
                  }}
                  aria-label="Close map"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>

              <div
                ref={(el) => {
                  mapContainerRef.current = el;
                  setMapContainerReady(!!el);
                }}
                className="flex-1"
                style={{ pointerEvents: 'auto' }}
              />

              <div className="p-4 border-t border-white/10 bg-[#0B0B0D]">
                <button
                  type="button"
                  onClick={handleConfirmLocationFromMap}
                  disabled={isSaving}
                  className="w-full rounded-full bg-[#F33363] px-6 py-4 text-sm font-bold text-white shadow-[0_12px_30px_rgba(243,51,99,0.45)] transition-transform hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
                >
                  {isSaving ? 'Confirming…' : 'Confirm Location'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* All Set Full Screen (portal to avoid overlay/drawer stacking issues) */}
      {allSetOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[250] bg-[#0B0B0D] flex flex-col items-center justify-center px-6 text-white">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="relative flex items-center justify-center w-24 h-24">
                <div className="absolute inset-0 rounded-full bg-[#F33363]/20 blur-[10px]" />
                <div className="relative w-20 h-20 rounded-full bg-[#F33363] flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-white" />
                </div>
              </div>
              <div className="mt-6 text-4xl font-black leading-tight">All Set!</div>
              <div className="mt-3 max-w-md text-white/70 text-sm">
                Your service location is saved. Update it anytime from Express Booking in the bottom bar.
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

